import { useState } from "react";
import { useTranslation } from "react-i18next";

import BidsListContainer from "@krea/customer-features/shared-components/bid-dashboards/commons/BidsListContainer";
import BidsListTable from "@krea/customer-features/shared-components/bid-dashboards/commons/BidsListTable";
import { useGetCachedCurrentLoanApplication } from "@krea/customer-features/store/current-loan-application/hooks";
import { useGetCachedLenderResponses } from "@krea/customer-features/store/lender-responses/hooks";
import { IRevolvingCreditBid } from "@krea/customer-features/types/lenderResponse";
import { RATE_TO_BID_KEY_MAP } from "@krea/customer-features/types/loanApplication";
import { ISortBidsBy, sortBids } from "@krea/customer-features/utils";

import BidsSort, { ISortingOptions } from "../bids-sort";
import { ILendersBids } from "../company-loan-bids";

import RevolvingCreditBidItem from "./RevolvingCreditBidItem";

const RevolvingCreditBids = ({
  onSelectLenderResponseClick,
  isSelecting,
  className,
  title,
  selectedLenderResponseID,
  recommendedLenderResponseID,
  goodAlternativeLenderResponseIDs,
}: ILendersBids) => {
  const { t } = useTranslation();
  const { data } = useGetCachedCurrentLoanApplication();
  const { id: loanApplicationID, customerPreferredRate } = data ?? {};

  const { data: bidsData } = useGetCachedLenderResponses(
    loanApplicationID ?? "",
  );
  const { revolvingCreditBids = [] } = bidsData ?? {};

  // Generally, the forecast should produce an attractiveness score for all bids or none at all (is something goes wrong).
  // We will check whether at least one bid has an attractiveness score to cover any odd cases where it might be missing for some bids.
  const hasAtLeastOneAttractivenessScore = revolvingCreditBids.some(
    (bid) => bid.lenderResponse.attractivenessScore,
  );
  const [sortBy, setSortBy] = useState<ISortBidsBy>(
    hasAtLeastOneAttractivenessScore
      ? { field: "attractivenessScore", order: "desc" }
      : { field: "limit", order: "asc" },
  );
  const sortedBids = sortBids(revolvingCreditBids, sortBy).filter(
    (bid) => bid.lenderResponse.id !== selectedLenderResponseID,
  );

  const rateKey = RATE_TO_BID_KEY_MAP[customerPreferredRate!];
  const rateTitle = t(`dash.commonBidsColumns.${rateKey}.title`);

  const sortingOptions: ISortingOptions[] = [
    {
      title: t("dash.bestOffer"),
      key: "attractivenessScore",
      order: "desc",
    },
    {
      title: t("dash.revolvingCreditBids.columns.limit.title"),
      key: "limit",
      order: "asc",
    },
    {
      title: rateTitle,
      key: rateKey,
      order: "asc",
    },
    // {
    //   title: t('dash.revolvingCreditBids.columns.averageMonthlyPayment.title'),
    //   key: 'averageMonthlyPayment',
    //   order: 'asc',
    // },
    // {
    //   title: t('dash.revolvingCreditBids.columns.amortisationPerMonth.title'),
    //   key: 'amortisationPerMonth',
    //   order: 'asc',
    // },
  ];

  const bidsListComponent = sortedBids.map((bid) => (
    <RevolvingCreditBidItem
      onSelectLenderResponseClick={onSelectLenderResponseClick}
      isSelecting={isSelecting}
      selectedLenderResponseID={selectedLenderResponseID}
      key={bid.id}
      bid={bid as IRevolvingCreditBid}
      rateKey={rateKey}
      isRecommended={recommendedLenderResponseID === bid.lenderResponse.id}
      isGoodAlternative={goodAlternativeLenderResponseIDs.some(
        (goodAlternativeLenderResponseID) =>
          goodAlternativeLenderResponseID === bid.lenderResponse.id,
      )}
    />
  ));

  return (
    sortedBids.length > 0 && (
      <BidsListContainer
        className={className}
        title={title}
        sort={
          sortedBids.length > 2 && (
            <BidsSort
              sortBy={sortBy}
              setSortBy={setSortBy}
              options={sortingOptions}
            />
          )
        }
        htmlDesktop={<BidsListTable>{bidsListComponent}</BidsListTable>}
        htmlMobile={<>{bidsListComponent}</>}
      />
    )
  );
};

export default RevolvingCreditBids;
